import React, { createContext, Dispatch, useReducer, useContext } from 'react';
import { setUserToken, setUserEmail } from 'utils/storage';

export type IUserRoles = 'GUEST' | 'VIEWER' | 'TESTER' | 'LIVE SERVICE' | 'MASTER' | 'OWNER'
export const UserRoles = ['GUEST', 'VIEWER', 'TESTER', 'LIVE SERVICE', 'MASTER']

export type IUserState = {
  user: {
    id: any;
    email: any;
    name: any;
    role: IUserRoles;
    has_bi_access?: boolean;
  };
  token: any;
  isLogin?: boolean;
};

const UserStateContext = createContext<IUserState | undefined>(undefined);

type IUserAction =
  | { type: 'SET'; data: IUserState }
  | { type: 'GET' }
  | { type: 'REMOVE' };

type UserDispatch = Dispatch<IUserAction>;

const UserDispatchContext = createContext<UserDispatch | undefined>(undefined);

function getInitialValues(): IUserState {
  return {
    user: {
      id: undefined,
      email: undefined,
      name: undefined,
      role: 'GUEST',
      has_bi_access: false
    },
    token: undefined,
    isLogin: false
  };
}

function reducer(state: IUserState, action: IUserAction): IUserState {
  switch (action.type) {
    case 'SET':
      const nextState = {
        ...state,
        ...action.data
      };

      return {
        ...nextState,
        isLogin: true
      };

    case 'GET':
      return state;

    case 'REMOVE':
      return getInitialValues();

    default:
      throw new Error('Unhandled action');
  }
}

export const UserContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, getInitialValues());

  return (
    <UserDispatchContext.Provider value={dispatch}>
      <UserStateContext.Provider value={state}>
        {children}
      </UserStateContext.Provider>
    </UserDispatchContext.Provider>
  );
};

export const useUserState = () => {
  const state = useContext(UserStateContext);

  if (state) {
    return state;
  } else {
    throw new Error('not-found user-state');
  }
};

export const useUserDispatch = () => {
  const dispatch = useContext(UserDispatchContext);

  if (dispatch) {
    return dispatch;
  } else {
    throw new Error('not-found user-dispatch');
  }
};

export const useUserActions = () => {
  const dispatch = useContext(UserDispatchContext);

  const updateUserState = ({
    user,
    token
  }: IUserState): IUserState => {
    setUserToken(token);
    setUserEmail(user.email);
    dispatch &&
      dispatch({
        type: 'SET',
        data: { user, token }
      });

    return { user, token };
  };

  const kickUser = () => {
    dispatch && dispatch({ type: 'REMOVE' });
  };

  return { updateUserState, kickUser };
};